import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { PublicNav, PublicFooter } from "./PublicHome";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Loader2, MapPin, Building2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import AdSlot from "../components/AdSlot";
import GrowthPartnerOpportunity from "../components/GrowthPartnerOpportunity";

export default function PublicJobDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [j, setJ] = useState(null);
  const [err, setErr] = useState("");
  const [gate, setGate] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(null);

  useEffect(() => {
    api.get(`/public/jobs/${id}`).then(r => setJ(r.data))
      .catch(e => setErr(e.response?.status === 404
          ? "This opportunity is no longer accepting applications."
          : "We couldn't load this job right now."));
  }, [id]);

  const apply = async () => {
    if (!user) { setGate(true); return; }
    if (user.role !== "candidate") {
      toast.error("Only candidate accounts may apply. Please sign in with a candidate account.");
      return;
    }
    setApplying(true);
    try {
      const { data } = await api.post(`/candidate-portal/jobs/${id}/apply`, {});
      setApplied(data);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "PROFILE_INCOMPLETE") {
        toast.error("Complete your candidate profile before applying.");
        nav(`/portal?next=/jobs/${id}`);
      } else {
        toast.error(typeof d === "string" ? d : "Application failed.");
      }
    } finally { setApplying(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <PublicNav/>
      <main className="max-w-4xl mx-auto px-6 py-10">
        <Link to="/jobs" data-testid="back-to-jobs" className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900 mb-6"><ArrowLeft size={12}/> Back to all jobs</Link>

        {!j && !err && (
          <div data-testid="jd-loading" className="text-sm text-slate-500 inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Loading…</div>
        )}
        {err && (
          <div data-testid="jd-unavailable" className="bg-white border border-slate-200 rounded-xl p-10 text-center">
            <div className="text-sm font-semibold text-slate-900">{err}</div>
            <Link to="/jobs" className="mt-4 inline-block text-xs font-semibold text-sky-600 hover:text-sky-700">Browse more jobs →</Link>
          </div>
        )}

        {j?.opportunity && <GrowthPartnerOpportunity j={j}/>}
        {j && !j.opportunity && !applied && (
          <div className="bg-white border border-slate-200 rounded-xl p-6 md:p-8" data-testid="jd-body">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-sky-600">{j.work_arrangement}{j.employment_type ? ` · ${j.employment_type}` : ""}</div>
            <h1 data-testid="jd-title" className="font-display font-bold text-3xl text-slate-900 tracking-tight mt-1">{j.job_title}</h1>
            <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-600">
              {j.employer_name && (<span className="flex items-center gap-1.5"><Building2 size={14}/> {j.employer_name}</span>)}
              <span className="flex items-center gap-1.5"><MapPin size={14}/> {j.location_display}</span>
            </div>
            {j.compensation && (
              <div className="mt-3 inline-block text-sm font-semibold text-slate-900 bg-slate-100 border border-slate-200 rounded px-3 py-1">
                {j.compensation.currency} {j.compensation.min?.toLocaleString()}–{j.compensation.max?.toLocaleString()} <span className="text-slate-500 font-normal">/ {j.compensation.period}</span>
              </div>
            )}

            <div className="prose prose-slate max-w-none mt-6 text-sm text-slate-800 whitespace-pre-wrap">{j.public_description}</div>

            {j.public_skills?.length > 0 && (
              <div className="mt-6">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-2">Skills</div>
                <div className="flex flex-wrap gap-1.5">
                  {j.public_skills.map(s => <span key={s} className="text-[11px] bg-sky-50 border border-sky-200 text-sky-800 rounded px-2 py-0.5">{s}</span>)}
                </div>
              </div>
            )}

            <div className="mt-8 pt-6 border-t border-slate-200 flex flex-wrap gap-3">
              <button onClick={apply} data-testid="apply-btn" disabled={applying}
                       className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-slate-900 text-white text-sm font-semibold disabled:opacity-50">
                {applying && <Loader2 size={14} className="animate-spin"/>} Apply now
              </button>
              {!user && (
                <Link to={`/portal/signup?next=/jobs/${j.public_job_id}`} data-testid="jd-create-account"
                       className="inline-flex items-center gap-2 h-11 px-5 rounded-md border border-slate-300 text-slate-800 text-sm font-semibold">
                  Create free candidate account
                </Link>
              )}
            </div>
            <div className="mt-8"><AdSlot placement="job_detail"/></div>
          </div>
        )}

        {applied && (
          <div className="bg-white border border-slate-200 rounded-xl p-8 text-center" data-testid="jd-applied">
            <CheckCircle2 size={36} className="text-emerald-600 mx-auto mb-3"/>
            <div className="font-display font-bold text-2xl text-slate-900">{applied.duplicate ? "Already applied" : "Application submitted"}</div>
            <p className="text-sm text-slate-600 mt-1">{applied.duplicate ? "You've already applied to this role." : "Your application has been submitted successfully."}</p>
            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              <Link to="/portal" className="h-10 px-5 rounded-md bg-slate-900 text-white text-sm font-semibold inline-flex items-center">View my candidate portal</Link>
              <Link to="/jobs" className="h-10 px-5 rounded-md border border-slate-300 text-slate-800 text-sm font-semibold inline-flex items-center">Browse more jobs</Link>
            </div>
          </div>
        )}

        {gate && !applied && (
          <div className="fixed inset-0 z-30 bg-slate-900/50 flex items-center justify-center p-4" onClick={()=>setGate(false)}>
            <div onClick={e=>e.stopPropagation()} className="w-full max-w-md bg-white rounded-xl p-6" data-testid="apply-gate">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-sky-600 mb-1">Apply for this role</div>
              <h3 className="font-display font-bold text-xl text-slate-900 mb-4">Sign in or create your free account</h3>
              <Link to={`/login?next=/jobs/${j.public_job_id}`} data-testid="gate-signin"
                     className="block w-full text-center h-11 leading-[44px] rounded-md bg-slate-900 text-white text-sm font-semibold mb-3">Sign in to apply</Link>
              <Link to={`/portal/signup?next=/jobs/${j.public_job_id}`} data-testid="gate-signup"
                     className="block w-full text-center h-11 leading-[44px] rounded-md border border-slate-300 text-slate-800 text-sm font-semibold">Create free candidate account</Link>
              <div className="text-[11px] text-slate-500 mt-3 text-center">Candidates never pay to apply, be matched, interview, or get hired.</div>
            </div>
          </div>
        )}
      </main>
      <PublicFooter/>
    </div>
  );
}
